/**
 * EnrollScreen (S0c) — one-time face enrolment.
 * Props: { ctx, api, tg, nav, state, setState, params } (see App.jsx).
 *
 *   - Capture a face still via <input type="file" accept="image/*" capture="user">
 *     (getUserMedia is BROKEN in the Telegram webview).
 *   - Read the file → base64, STRIP the "data:...;base64," prefix → image_b64.
 *   - MainButton "Rasmga olish" triggers the hidden input; on pick → api.enroll.
 *   - on ok: setState({session:{...,enrolled:true}}) then nav.replace(LANDING).
 *   - on error: ERROR_UI (NO_CONSENT/MISSING_IMAGE/NO_FACE/FACE_SERVICE_DOWN).
 */
import { useEffect, useRef, useState } from 'react';
import Screen from '../components/Screen.jsx';
import Button from '../components/Button.jsx';
import Spinner from '../components/Spinner.jsx';
import ErrorState from '../components/ErrorState.jsx';
import { T } from '../lib/i18n.js';
import { SCREENS } from '../App.jsx';

/** Read a File to a bare base64 payload (data: prefix stripped). */
function fileToB64(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => {
      const s = String(r.result || '');
      const comma = s.indexOf(',');
      resolve(comma >= 0 ? s.slice(comma + 1) : s);
    };
    r.onerror = () => reject(new Error('read'));
    r.readAsDataURL(file);
  });
}

export default function EnrollScreen({ tg, api, nav, state, setState }) {
  const inputRef = useRef(null);
  const [preview, setPreview] = useState(null); // object URL for the ring
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  function pick() {
    setErr(null);
    if (inputRef.current) inputRef.current.click();
  }

  async function onFile(e) {
    const file = e.target.files && e.target.files[0];
    e.target.value = ''; // allow re-picking the same file
    if (!file) return;
    setPreview(URL.createObjectURL(file));
    setBusy(true);
    setErr(null);
    tg.MainButton.showProgress();
    let image_b64;
    try {
      image_b64 = await fileToB64(file);
    } catch (_e) {
      tg.MainButton.hideProgress();
      setBusy(false);
      setErr('MISSING_IMAGE');
      return;
    }
    const res = await api.enroll(image_b64);
    tg.MainButton.hideProgress();
    setBusy(false);
    if (res && res.ok) {
      tg.haptic('success');
      setState({ session: { ...(state.session || {}), enrolled: true } });
      nav.replace(SCREENS.LANDING);
    } else {
      tg.haptic('error');
      setErr((res && res.error) || 'SERVER_ERROR');
    }
  }

  useEffect(() => {
    if (!tg.isTelegram) return undefined;
    return tg.MainButton.onClick(pick);
  });

  useEffect(() => {
    if (!tg.isTelegram) return undefined;
    tg.MainButton.setText(T.btn_capture);
    tg.MainButton.show();
    return () => tg.MainButton.hide();
  }, [tg]);

  const hiddenInput = (
    <input
      ref={inputRef}
      className="capture-input"
      type="file"
      accept="image/*"
      capture="user"
      onChange={onFile}
    />
  );

  if (err) {
    return (
      <Screen title={T.enroll_h}>
        {hiddenInput}
        <ErrorState code={err} onRecover={() => { setErr(null); pick(); }} recover="retry" />
      </Screen>
    );
  }

  return (
    <Screen
      title={T.enroll_h}
      footer={
        !tg.isTelegram ? (
          <Button onClick={pick} disabled={busy}>
            {T.btn_capture}
          </Button>
        ) : null
      }
    >
      <p style={{ color: 'var(--ink-2)', fontSize: 14, margin: 0 }}>{T.enroll_b}</p>
      {hiddenInput}
      <div className="facewrap">
        <div className="ring">
          <div className="inner">
            {preview ? <img src={preview} alt="" /> : '🙂'}
            {busy ? <div className="scan" /> : null}
          </div>
        </div>
        {busy ? <Spinner size="md" label={T.loading} /> : null}
        <div className="lil">{T.face_privacy}</div>
      </div>
    </Screen>
  );
}
